import { Assert } from '@aura/core-lib';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { useAuth } from '../../../auth/auth.hook';
import { useConfig } from '../../../config/config.hook';
import { useMatriculaAtiva } from '../../../matricula-ativa/use-matricula-ativa.hook';
import { PerfilAluno, useUsuario } from '../../../usuario';
import { consultarTemaService } from '../../services/consultar-tema/consultar-tema.service';
import { ConsultarTemaApiResponse, ConteudoApiResponse } from '../../services/consultar-tema/consultar-tema.types';
import { Conteudo, StatusConclusaoConteudo, Tema, UseConsultarTemaPorIdHook } from './use-consultar-tema-por-id.types';

export const useConsultarTemaPorId: UseConsultarTemaPorIdHook = ({ idTurma, idTema }) => {
  const { token } = useAuth();
  const { get: getUsuario } = useUsuario();

  const usuario = getUsuario<PerfilAluno>();

  const emailUtilizado = useMemo(() => usuario.aura.dados.emailUtilizado, [usuario.aura.dados.emailUtilizado]);

  const matriculaAtiva = useMatriculaAtiva();

  const urlBase = useConfig().getValueOrThrow<string>('API_DEDICADA');

  const { data, isValidating, isLoading, error, mutate } = useSWR<ConsultarTemaApiResponse>(
    token && emailUtilizado && idTema ? ['consultar-tema-por-id', emailUtilizado, idTurma, idTema] : null,
    () => {
      Assert.nonNullable(idTema, () => 'idTema não pode ser nulo');
      return consultarTemaService({
        urlBase,
        token,
        idTurma,
        idTema,
        query: {
          matricula: matriculaAtiva?.matricula ?? '',
        },
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: true,
      shouldRetryOnError: false,
      dedupingInterval: 30000,
    },
  );

  const tema: Tema | undefined = useMemo(() => {
    const mapConteudo = (conteudo: ConteudoApiResponse): Conteudo => {
      const statusConclusao = ((): StatusConclusaoConteudo | undefined => {
        if (!conteudo.conclusivel) return undefined;

        Assert.nonNullable(conteudo.statusConclusao);

        return {
          concluido: conteudo.statusConclusao.concluido,
          dataConclusao: conteudo.statusConclusao.dataConclusao
            ? DateTime.fromISO(conteudo.statusConclusao.dataConclusao)
            : null,
          tempoRestanteConclusaoEmSegundos: conteudo.statusConclusao.tempoRestanteConclusaoEmSegundos,
          tempoMinimoConclusaoConteudo: conteudo.statusConclusao.tempoMinimoConclusaoConteudo,
        };
      })();

      if (conteudo.tipo === 'PHORTE') {
        return {
          id: conteudo.id,
          tipo: 'PHORTE',
          ordem: conteudo.ordem,
          titulo: conteudo.titulo,
          conclusivel: conteudo.conclusivel,
          statusConclusao,
        };
      }

      Assert.nonNullable(conteudo.url);

      return {
        id: conteudo.id,
        tipo: 'LINK',
        ordem: conteudo.ordem,
        titulo: conteudo.titulo,
        url: conteudo.url,
        conclusivel: conteudo.conclusivel,
        statusConclusao,
      };
    };

    if (data) {
      return {
        id: data.id,
        titulo: data.titulo,
        ordem: data.ordem,
        rotulo: data.rotulo,
        categoria: data.categoria,
        conteudos: data.conteudos.map(mapConteudo),
      };
    }
  }, [data]);

  const pendente = useMemo(
    () => (!data && !error) || (!!error && (isValidating || isLoading)),
    [data, error, isValidating, isLoading],
  );

  const retentativa = useCallback(() => {
    mutate();
  }, [mutate]);

  const marcarComoConcluido = useCallback(
    (idObjeto: string) => {
      mutate((temaAtual) => {
        if (!temaAtual) return;

        const conteudo = temaAtual.conteudos.find((c) => c.id === idObjeto);

        if (!conteudo) return;

        const conteudoAtualizado: ConteudoApiResponse = {
          ...conteudo,
          statusConclusao: {
            concluido: true,
            dataConclusao: DateTime.now().toISO(),
            tempoRestanteConclusaoEmSegundos: 0,
            tempoMinimoConclusaoConteudo: 0,
          },
        };

        const conteudos = temaAtual.conteudos.map((c) => (c.id === idObjeto ? conteudoAtualizado : c));

        return {
          ...temaAtual,
          conteudos,
        };
      }, false);
    },
    [mutate],
  );

  const atualizarTempoRestante = useCallback(
    (idConteudo: string, novoTempo: number) => {
      mutate((temaAtual) => {
        if (!temaAtual) return;

        const conteudo = temaAtual.conteudos.find((c) => c.id === idConteudo);

        if (!conteudo) return;

        const conteudoAtualizado: ConteudoApiResponse = {
          ...conteudo,
          statusConclusao: {
            concluido: false,
            dataConclusao: null,
            tempoRestanteConclusaoEmSegundos: novoTempo,
            tempoMinimoConclusaoConteudo: conteudo.statusConclusao
              ? conteudo.statusConclusao.tempoMinimoConclusaoConteudo
              : 0,
          },
        };

        const conteudos = temaAtual.conteudos.map((c) => (c.id === idConteudo ? conteudoAtualizado : c));

        return {
          ...temaAtual,
          conteudos,
        };
      }, false);
    },
    [mutate],
  );

  return {
    tema,
    pendente,
    erro: !!error,
    retentativa,
    marcarComoConcluido,
    atualizarTempoRestante,
  };
};
